import React, { useState, useRef, useEffect } from 'react';
import {
  Plus,
  FolderOpen,
  FileCode,
  Files,
  Clock,
  Play,
  Plugs,
  Robot,
  Cpu,
  ChatDots,
  Terminal
} from '@phosphor-icons/react';

const CapabilityLauncher = ({
  hasActiveProject = false,
  onOpenProjects,
  onSelectSurface,
  onSelectTab,
  onOpenConnectors,
  onOpenOrigin,
  onOpenProduction,
  onOpenChat,
  onOpenActivity,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const launcherRef = useRef(null);

  // Close on click outside and escape key
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (launcherRef.current && !launcherRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const capabilityGroups = [
    {
      group: 'TRABAJO',
      items: [
        {
          id: 'projects',
          label: 'Proyecto',
          icon: FolderOpen,
          action: () => {
            if (onOpenProjects) onOpenProjects();
          }
        },
        {
          id: 'files',
          label: 'Archivos',
          icon: Files,
          requiresProject: true,
          action: () => {
            if (onSelectSurface) onSelectSurface('workspace');
            if (onSelectTab) onSelectTab('files');
          }
        },
        {
          id: 'preview',
          label: 'Vista Previa',
          icon: FileCode,
          action: () => {
            if (onSelectSurface) onSelectSurface('workspace');
            if (onSelectTab) onSelectTab('preview');
          }
        },
        {
          id: 'executions',
          label: 'Ejecuciones',
          icon: Clock,
          requiresProject: true,
          action: () => {
            if (onSelectSurface) onSelectSurface('workspace');
            if (onSelectTab) onSelectTab('executions');
          }
        },
        {
          id: 'sandbox',
          label: 'Sandbox',
          icon: Play,
          requiresProject: true,
          action: () => {
            if (onSelectSurface) onSelectSurface('workspace');
            if (onSelectTab) onSelectTab('sandbox');
          }
        }
      ]
    },
    {
      group: 'IA Y CONEXIÓN',
      items: [
        {
          id: 'connectors',
          label: 'Conectores',
          icon: Plugs,
          action: () => {
            if (onOpenConnectors) onOpenConnectors();
          }
        },
        {
          id: 'origin',
          label: 'Origin AI',
          icon: Robot,
          action: () => {
            if (onOpenOrigin) onOpenOrigin();
          }
        },
        {
          id: 'production',
          label: 'Production AI',
          icon: Cpu,
          action: () => {
            if (onOpenProduction) onOpenProduction();
          }
        }
      ]
    },
    {
      group: 'COMUNICACIÓN / OBSERVABILIDAD',
      items: [
        {
          id: 'chat',
          label: 'Chat',
          icon: ChatDots,
          action: () => {
            if (onOpenChat) onOpenChat();
          }
        },
        {
          id: 'activity',
          label: 'Actividad',
          icon: Terminal,
          action: () => {
            if (onOpenActivity) onOpenActivity();
          }
        }
      ]
    }
  ];

  const handleSelectCapability = (cap) => {
    cap.action();
    setIsOpen(false);
  };

  return (
    <div ref={launcherRef} className={`relative inline-block ${className}`}>
      {/* Symbolic Trigger Button [+] */}
      <button
        type="button"
        data-testid="capability-launcher-btn"
        aria-label="Más capacidades"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className={`px-2 py-1 text-xs font-sans font-medium flex items-center justify-center rounded-md border transition-all ${
          isOpen
            ? 'bg-white/[0.08] text-white border-white/[0.18] shadow-xs'
            : 'bg-white/[0.03] text-slate-300 hover:text-white border-white/[0.08] hover:bg-white/[0.06]'
        }`}
        title="Más capacidades"
      >
        <Plus
          size={14}
          weight="bold"
          className={`transition-transform duration-150 ${isOpen ? 'rotate-45 text-slate-200' : 'text-slate-300'}`}
        />
      </button>

      {/* Floating Compact Capability Popover Menu */}
      {isOpen && (
        <div
          data-testid="capability-launcher-menu"
          role="menu"
          aria-label="Menú de capacidades"
          className="absolute top-full left-0 mt-1.5 w-60 bg-[#11131A] border border-white/[0.1] rounded-xl shadow-2xl p-1.5 z-50 backdrop-blur-md flex flex-col gap-1 text-xs select-none animate-in fade-in zoom-in-95 duration-100"
        >
          {capabilityGroups.map((grp, gIdx) => (
            <div key={grp.group} className={gIdx > 0 ? 'pt-1 border-t border-white/[0.05]' : ''}>
              <div className="px-2 py-0.5 text-[10px] font-mono font-medium tracking-wider text-slate-500 uppercase">
                {grp.group}
              </div>
              <div className="space-y-0.5 mt-0.5">
                {grp.items.map((cap) => {
                  const Icon = cap.icon;
                  const isMissingProject = cap.requiresProject && !hasActiveProject;
                  return (
                    <button
                      key={cap.id}
                      type="button"
                      role="menuitem"
                      data-testid={`capability-item-${cap.id}`}
                      onClick={() => handleSelectCapability(cap)}
                      className="w-full px-2 py-1.5 flex items-center justify-between rounded-md hover:bg-white/[0.06] text-slate-300 hover:text-white transition-colors text-left group"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon size={14} className="text-slate-400 group-hover:text-cyan-400 transition-colors shrink-0" />
                        <span className="font-medium text-xs font-sans text-slate-200 group-hover:text-white truncate">
                          {cap.label}
                        </span>
                      </div>
                      {isMissingProject && (
                        <span className="text-[9px] text-amber-400/80 font-sans shrink-0 ml-1">
                          (Requiere proyecto)
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CapabilityLauncher;
